import { useMutation, useQuery, useQueryClient } from 'react-query'
import { api } from '../../../utils/api'
import { downloadFile } from '../../../utils/helper'
import {
  IDcaRecord,
  IDcaRecordList,
  IExporDcaRecordReportParams,
  IImportDcaFileBody,
  IImportDcaFileList,
} from './import-dca-typed'
import { ContentType } from '../../../utils/api/tools'
import dayjs from 'dayjs'

const IMPORT_DCA_FILE_PATH = 'internal/import-dca-files'
const IMPORT_DCA_FILE_TEMPLATE_PATH = `${IMPORT_DCA_FILE_PATH}/template`
const IMPORT_DCA_FILE_UPLOAD_PATH = `${IMPORT_DCA_FILE_PATH}/upload`
const IMPORT_DCA_RCORD_PATH = `${IMPORT_DCA_FILE_PATH}/record`
const DCA_RCORD_REPORT = `${IMPORT_DCA_FILE_PATH}/report`

export const useUploadImportDcaFile = () => {
  const queryClient = useQueryClient()
  return useMutation<any, any, IImportDcaFileBody>(
    async ({ file, otpData: { otp, otpId } }) => {
      const { data } = await api.savvyGold.post(
        `${IMPORT_DCA_FILE_UPLOAD_PATH}`,
        { file, otp, otpId },
        {
          contentType: ContentType.FORMDATA,
        },
      )
      return data
    },
    {
      onSuccess: () => {
        queryClient.refetchQueries([IMPORT_DCA_FILE_PATH])
      },
    },
  )
}

export const useGetDcaRecordById = (id: string) => {
  return useQuery([IMPORT_DCA_RCORD_PATH, id], async () => {
    const { data } = await api.savvyGold.get<IDcaRecord>(`${IMPORT_DCA_RCORD_PATH}/${id}`)
    const result = data.data

    return result
  })
}

export const useGetDcaRecord = (dcaFileId: string, params?: { q?: string; page?: number }) => {
  return useQuery(
    [IMPORT_DCA_FILE_PATH, dcaFileId, params],
    async () => {
      const { data } = await api.savvyGold.get<IDcaRecordList>(
        `${IMPORT_DCA_FILE_PATH}/${dcaFileId}/record`,
        {
          page: params?.page,
          limit: 10,
          q: params?.q,
        },
      )
      const result = data.data

      return result
    },
    {
      keepPreviousData: true,
      refetchInterval: 10 * 1000,
    },
  )
}

export const useGetImportDcaFiles = (params?: { q?: string; page?: number }) => {
  return useQuery(
    [IMPORT_DCA_FILE_PATH, params],
    async () => {
      const { data } = await api.savvyGold.get<IImportDcaFileList>(IMPORT_DCA_FILE_PATH, {
        page: params?.page,
        limit: 10,
        q: params?.q,
      })
      const result = data.data

      return result
    },
    {
      keepPreviousData: true,
      refetchInterval: 10 * 1000,
    },
  )
}

export const useGetImportFileTemplate = () => {
  return useMutation<any, string>(async () => {
    const res = await api.savvyGold.getFile(`${IMPORT_DCA_FILE_TEMPLATE_PATH}`)
    downloadFile(res)
  })
}

export const useGetDcaRecordReport = () => {
  return useMutation<any, string, IExporDcaRecordReportParams>(
    async (params: IExporDcaRecordReportParams) => {
      const { startDate, endDate, ...restParams } = params
      const res = await api.savvyGold.getFile(`${DCA_RCORD_REPORT}`, {
        startDate: dayjs(startDate).format(),
        endDate: dayjs(endDate).format(),
        ...restParams,
      })
      downloadFile(res)
    },
  )
}
